import axios from "axios";
import { baseUrl } from "./baseUrl";
import { getAuthToken } from "./authSession";

export const LOCATION_MIN_DISTANCE_METERS = 100;
export const LOCATION_MIN_INTERVAL_MS = 60_000;

let watchId = null;
let heartbeatTimer = null;
let lastSentCoords = null;
let lastSentTime = 0;
let sendInFlight = false;

const toRadians = (value) => (value * Math.PI) / 180;

export const distanceInMeters = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadius * c;
};

export const shouldSendLocation = (lat, lon, now = Date.now()) => {
  if (!lastSentCoords) {
    return true;
  }

  const movedDistance = distanceInMeters(
    lastSentCoords.lat,
    lastSentCoords.lon,
    lat,
    lon
  );

  if (movedDistance >= LOCATION_MIN_DISTANCE_METERS) {
    return true;
  }

  return now - lastSentTime >= LOCATION_MIN_INTERVAL_MS;
};

export const sendLocationToServer = async (lat, lon) => {
  if (!getAuthToken() || sendInFlight) {
    return false;
  }

  sendInFlight = true;

  try {
    const data = new FormData();
    data.append("lat", lat);
    data.append("lng", lon);

    await axios.post(`${baseUrl}/location`, data);
    lastSentCoords = { lat, lon };
    lastSentTime = Date.now();
    return true;
  } catch (error) {
    console.error("Error sending location:", error?.message || error);
    return false;
  } finally {
    sendInFlight = false;
  }
};

const handlePosition = (position) => {
  if (!getAuthToken()) {
    stopLocationTracker();
    return;
  }

  const { latitude, longitude } = position.coords;

  if (!shouldSendLocation(latitude, longitude)) {
    return;
  }

  sendLocationToServer(latitude, longitude);
};

const requestCurrentPosition = () => {
  if (!getAuthToken() || !navigator.geolocation) {
    return;
  }

  navigator.geolocation.getCurrentPosition(handlePosition, (error) => {
    console.error("Error getting user location:", error.message);
  }, {
    enableHighAccuracy: true,
    maximumAge: 10_000,
    timeout: 20_000,
  });
};

export const startLocationTracker = () => {
  if (!getAuthToken() || !navigator.geolocation) {
    stopLocationTracker();
    return;
  }

  if (watchId !== null) {
    return;
  }

  requestCurrentPosition();

  watchId = navigator.geolocation.watchPosition(
    handlePosition,
    (error) => {
      console.error("Error watching user location:", error.message);
    },
    {
      enableHighAccuracy: true,
      maximumAge: 10_000,
      timeout: 20_000,
    }
  );

  heartbeatTimer = window.setInterval(
    requestCurrentPosition,
    LOCATION_MIN_INTERVAL_MS
  );

  document.addEventListener("visibilitychange", handleVisibilityChange);
};

export const stopLocationTracker = () => {
  if (watchId !== null && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }

  if (heartbeatTimer !== null) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }

  document.removeEventListener("visibilitychange", handleVisibilityChange);
  lastSentCoords = null;
  lastSentTime = 0;
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible" && getAuthToken()) {
    requestCurrentPosition();
  }
};
